import { useState } from 'react';
import type { Screen } from '../pipeline/screen';
import { Refused } from './credential/CredentialShell';

/**
 * Starting a run, watching it, and what a person is told when it stops
 * (docs/DECISIONS.md ZD-124 §4).
 *
 * Three of `screenFor`'s screens, rendered from `next` and nothing else:
 *
 * - **ready** — a stage can run and nobody has pressed. The control is the
 *   person saying the documents are complete (ZD-121 §5.1), and the label
 *   beside it is the stage's own sentence for what will happen.
 * - **running** — a step is scheduled or executing. The page polls while
 *   this is what `next` says (`pipeline/poll.ts`) and shows the stage's
 *   label; there is no control, because a run in flight is the driver's.
 * - **stopped** — the last run did not advance. What is shown is the run's
 *   own `status` and `reason`, verbatim, and its id for anyone asking about
 *   it, never a sentence composed here; the control is the retry, which is a
 *   person's press (ZD-123 §3).
 *
 * A press is `POST /advance`; a 202 and both 409s carry `next`, which the
 * page adopts, so what this shows after a press is what the API said and
 * never what the page assumed.
 */
export function RunControl({
  screen,
  onAdvance,
}: {
  screen: Extract<Screen, { kind: 'ready' | 'running' | 'stopped' }>;
  onAdvance: () => Promise<void>;
}): React.JSX.Element {
  const [pressing, setPressing] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const press = async (): Promise<void> => {
    setRefusal(null);
    setPressing(true);
    try {
      await onAdvance();
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : String(error));
    } finally {
      setPressing(false);
    }
  };

  if (screen.kind === 'running') {
    return (
      <section className="run run--running" aria-label="Running" aria-live="polite">
        <h2>Running</h2>
        <p className="run__stage">
          <span className="run__pulse" aria-hidden="true" />
          {screen.stage.label}…
        </p>
        <p className="run__note">
          This page checks every couple of seconds and moves on by itself when the run needs you or
          stops.
        </p>
      </section>
    );
  }

  return (
    <section
      className={screen.kind === 'stopped' ? 'run run--stopped' : 'run'}
      aria-label={screen.kind === 'stopped' ? 'The run stopped' : 'Ready to run'}
    >
      {screen.kind === 'stopped' ? (
        <>
          <h2>The run stopped</h2>
          <p className="run__reason">
            <span className="run__status">{screen.run.status}</span> — {screen.run.reason}
          </p>
          <p className="run__note">
            Run <code>{screen.run.run_id}</code>. Nothing is retried on its own; pressing again runs
            the next step, which is {screen.stage.label.toLowerCase()}.
          </p>
        </>
      ) : (
        <>
          <h2>Ready to run</h2>
          <p className="run__note">
            When the documents are complete, start the analysis. The first step is{' '}
            {screen.stage.label.toLowerCase()}; the run carries on by itself and stops where it
            needs you.
          </p>
        </>
      )}
      {refusal === null ? null : <Refused message={refusal} />}
      <div className="run__actions">
        <button
          type="button"
          className="submit"
          disabled={pressing}
          onClick={() => {
            void press();
          }}
        >
          {pressing ? 'Starting…' : screen.kind === 'stopped' ? 'Run again' : 'Start the analysis'}
        </button>
      </div>
    </section>
  );
}
